import { Crown, Skull } from "lucide-react";
import type { LmsComp, LmsPlayer } from "../../lib/lms";
import { TeamBadge } from "./TeamBadge";
import { PlayerMenu } from "./PlayerMenu";

const REASON = { lost: "Team lost", drew: "Team drew", no_pick: "No pick" };

function PickCell({ comp, p }: { comp: LmsComp; p: LmsPlayer }) {
  if (!p.alive || comp.status === "finished") return null;
  if (p.pick) return <span className="flex items-center gap-1.5 text-xs font-extrabold"><TeamBadge team={p.pick} size="sm" /><span className="hidden truncate sm:inline">{p.pick}</span></span>;
  if (comp.window === "pending") return <span className="text-xs font-bold text-[#0B1F33]/50">—</span>;
  return p.picked
    ? <span className="rounded-full bg-[#35E07A] px-2 py-0.5 text-[10px] font-extrabold uppercase">Picked ✓</span>
    : <span className="rounded-full bg-[#FFE14D] px-2 py-0.5 text-[10px] font-extrabold uppercase">{comp.window === "closed" ? "No pick!" : "Not yet"}</span>;
}

function Row({ comp, p, meId, onChange }: { comp: LmsComp; p: LmsPlayer; meId?: string; onChange: (c: LmsComp) => void }) {
  const winner = comp.winner_id === p.id;
  return (
    <li className={`flex items-center gap-2 rounded-xl border-2 px-2.5 py-1.5 ${p.alive ? "border-[#0B1F33] bg-white" : "border-[#0B1F33]/20 bg-[#F1F1F1] text-[#0B1F33]/60"}`} data-testid={`lms-standing-${p.id}`}>
      {winner ? <Crown className="h-4 w-4 shrink-0 text-[#E0A800]" strokeWidth={3} /> : !p.alive && <Skull className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
      <span className={`min-w-0 flex-1 truncate font-comic text-sm ${p.alive ? "" : "line-through"}`}>{p.name}{p.id === meId && <span className="ml-1 rounded bg-[#0B1F33] px-1 text-[10px] text-white no-underline">YOU</span>}</span>
      {!p.alive && <span className="text-[10px] font-extrabold uppercase" data-testid={`lms-out-reason-${p.id}`}>GW{p.eliminated_gw} · {p.reason ? REASON[p.reason] : ""}</span>}
      <PickCell comp={comp} p={p} />
      {comp.is_admin && <PlayerMenu comp={comp} p={p} onChange={onChange} />}
    </li>
  );
}

export function Standings({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const alive = comp.players.filter((p) => p.alive);
  const out = comp.players.filter((p) => !p.alive).sort((a, b) => (b.eliminated_gw || 0) - (a.eliminated_gw || 0));
  return (
    <section className="grid gap-2 md:grid-cols-2" data-testid="lms-standings">
      <div className="rounded-[22px] border-[3px] border-[#0B1F33] bg-[#E3F7EA] p-3 shadow-[0_4px_0_#0B1F33]">
        <h2 className="ua-title text-2xl text-[#0E7A3E]">Still in ({alive.length})</h2>
        <p className="text-[11px] font-bold text-[#0B1F33]/60">{comp.window === "open" ? "Picks are hidden until the window closes." : `This week's selections — GW ${comp.gameweek}`}</p>
        <ul className="mt-2 space-y-1.5" data-testid="lms-alive-list">{alive.map((p) => <Row key={p.id} comp={comp} p={p} meId={comp.me?.id} onChange={onChange} />)}</ul>
        {!alive.length && <p className="text-sm font-bold">No players yet.</p>}
      </div>
      <div className="rounded-[22px] border-[3px] border-[#0B1F33] bg-white p-3 shadow-[0_4px_0_#0B1F33]">
        <h2 className="ua-title text-2xl text-[#C8102E]">Eliminated ({out.length})</h2>
        <ul className="mt-2 space-y-1.5" data-testid="lms-out-list">{out.map((p) => <Row key={p.id} comp={comp} p={p} meId={comp.me?.id} onChange={onChange} />)}</ul>
        {!out.length && <p className="mt-1 text-sm font-bold text-[#0B1F33]/60">Nobody's out… yet.</p>}
      </div>
    </section>
  );
}

const RESULT_TONE = { W: "bg-[#35E07A]", D: "bg-[#FFE14D]", L: "bg-[#FF5D5D] text-white" };

export function GameweekHistory({ comp }: { comp: LmsComp }) {
  if (!comp.history.length) return null;
  const name = (id: string) => comp.players.find((p) => p.id === id)?.name || "Removed player";
  return (
    <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-white p-3 shadow-[0_4px_0_#0B1F33]" data-testid="lms-history">
      <h2 className="ua-title text-2xl text-[#0B1F33]">Gameweek results</h2>
      <div className="mt-2 grid gap-1.5 md:grid-cols-2">
        {[...comp.history].reverse().map((gw) => (
          <div key={gw.number} className="rounded-xl border-2 border-[#0B1F33]/20 bg-[#F4FBF6] p-2" data-testid={`lms-history-gw-${gw.number}`}>
            <p className="flex items-center justify-between font-comic text-sm">GW {gw.number}{gw.rollover && <span className="rounded-full bg-[#FF5D1F] px-2 text-[10px] text-white">Everyone out → rollover, all back in</span>}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(gw.results).map(([team, r]) => <span key={team} className={`flex items-center gap-1 rounded-full border-2 border-[#0B1F33] py-0.5 pl-0.5 pr-2 text-[10px] font-extrabold ${RESULT_TONE[r]}`}><TeamBadge team={team} size="sm" />{r}</span>)}
            </div>
            {!!Object.keys(gw.eliminated).length && <p className="mt-1 text-[11px] font-bold text-[#C8102E]">Out: {Object.keys(gw.eliminated).map(name).join(", ")}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
